/**
 * Pack-size rule, checked against the actual cart (window.LunaPackRule).
 *
 * A product is sold in packs when its custom.pack_size metafield is above 1.
 * Its allowed cart TOTALS (all of its cart rows added up) are 0, any whole
 * number of packs, exactly 1 when custom.single_sample is true, and ALL the
 * stock left (Oliver, 2026-10-14: "if we actually only have 13 left it's
 * fine to sell them all; we just don't want to be breaking apart packs of
 * 10 unnecessarily"). The stock left is known only when Shopify tracks the
 * variant and overselling is off (snippets/pack-rule-available.liquid);
 * otherwise that case never applies. Singles never stack: 1 + 1, 1 + 10 and
 * 10 + 1 are all invalid (unless the total is all the stock left).
 *
 * The rule's Liquid home is snippets/pack-rule.liquid (the only reader of
 * the metafields) and snippets/pack-rule-check.liquid (valid / up / down).
 * The page this runs on can be stale, so every decision here is made on a
 * fresh read of /cart?view=pack-rules (templates/cart.pack-rules.liquid).
 *
 * Loading, in two parts (2026-10-14, so nothing holds up the first render):
 *  - snippets/pack-rule-hook.liquid, inline in the <head>: wraps
 *    window.fetch before any app script runs, counts cart writes in flight
 *    and queues add responses in window.__lunaPack (see its comment).
 *  - this file, deferred after theme.js: on load it drains that queue and
 *    takes over. theme.js only calls in through window.LunaPackRule, so if
 *    this file never loads, the theme works as before.
 *
 * What it does:
 *  - Product page: before the theme posts /cart/add.js
 *    (theme.AjaxProduct.addItemFromForm calls beforeAdd), refuse an add that
 *    would leave an invalid total and say why next to the button. An add
 *    above the stock left is judged on what Shopify will make of it: Shopify
 *    caps it at the stock left (answering 422 "Only 13 items were added to
 *    your cart due to availability." AFTER adding them), and all the stock
 *    left is valid, so it is let through; when it comes back capped it is
 *    treated as a success: Shopify's words shown as a note, the cart opened.
 *    While the page shows a single already in the cart, the note under Add
 *    to cart reads "Your single will be replaced with <qty>" (the dropdown's
 *    choice, kept current as it changes), and Add replaces the single
 *    instead of adding to it: one /cart/change.js on the single's row, never
 *    add-then-fix, and no /cart/add.js. If the fresh read finds the single
 *    gone, Add is a normal add. The dropdown offers only what the stock
 *    allows, with the remainder as "13 (all remaining)" (addOptions). When
 *    the cart already holds all the stock left (allInCart), nothing can be
 *    added: the note reads "You already have all 13 we have in your cart.",
 *    Add is disabled and the dropdown hidden, until the total drops. The
 *    page is redrawn from one fresh read when the drawer changes the cart
 *    (cart:updated, cart:build) or the page is shown again (pageshow from
 *    the back/forward cache, visibilitychange), debounced.
 *  - Cart page and drawer: the warning that snippets/cart-item.liquid draws
 *    on a broken product has fix buttons; they set the product's first row
 *    to the target and its other rows to 0, by line key.
 *  - Checkout (theme.CartForm.onSubmit calls checkoutGate): wait for cart
 *    work (below), re-check the cart, then block (theme setting
 *    pack_rule_block_checkout, the default) or, only with the setting off,
 *    record a private __pack_rule_warning cart attribute (hidden at
 *    checkout, kept on the order; written on every gated checkout, '' when
 *    valid, as it cannot be read back) and continue. The form is always
 *    submitted without its positional updates[] fields, which could
 *    overwrite live quantities. Waiting: nothing pending, no wait at all;
 *    the theme's quantity debounces, at most settleMaxMs (4 s); cart writes
 *    in flight (the hook's count, and this file's background work), up to
 *    busyCapMs (10 s), and if they are still in flight then, refuse with
 *    "Your cart is still updating ..." rather than judge a stale cart. A
 *    failed or slow (5 s) rules read lets checkout go ahead, and so does a
 *    throw while resubmitting.
 *  - Any other /cart/add.js (the Also Bought app, the grid quick add): the
 *    app gets Shopify's response at once, untouched; the check runs in the
 *    background (counted as cart work, so checkout waits for it): on a
 *    fresh read, for each product sold in packs that it added (the added
 *    quantities come from the request body):
 *      - already in the cart before this add (Oliver, 2026-10-13: "it should
 *        just not increment"): undo the add with ONE /cart/change.js on the
 *        line the add landed on, setting it to what it held before (0 for a
 *        row the add created, or the old quantity when the add merged into a
 *        row). The total is then what it was, even a broken one, which stays
 *        flagged. Rebuild and open the cart, and once the cart list has been
 *        rebuilt, show cart.pack.already_in_cart ("Already in your cart --
 *        change the quantity here.", from the row's data-msg-already) on
 *        that product's first row (re-drawn by rebuilds in the next 5 s; a
 *        reload onto /cart in the next 15 s shows it once, through
 *        sessionStorage). If the undo fails, the product's flag shows the
 *        error and the add stands for the flags to catch.
 *      - not in the cart before (or the body can't be read): a packs-only
 *        product whose only row is the single just added goes up to the
 *        next valid total (one pack, or the stock left if less); a single
 *        where singles are allowed stays. Then rebuild the cart.
 *      - refused by Shopify (e.g. a 422 for stock): re-read and redraw,
 *        show Shopify's words on the product's row and open the cart.
 *    The undo is a /cart/change.js, which the hook never queues, so it
 *    cannot loop.
 *  - A refused (non-OK) cart write is never taken to mean "nothing
 *    changed": Shopify applies a capped quantity before answering 422. After
 *    any failed write or add (a replace, a fix, an undo or bump, the
 *    product form's add, an app's add) the cart is read again and redrawn:
 *    the product page's state, the cart lists (cart:build) and so their
 *    flags, with Shopify's message shown. Our own /cart/change.js writes
 *    give up waiting after writeTimeoutMs (5 s), so a hung write cannot
 *    freeze the Add or fix buttons; the request itself is not cancelled, so
 *    checkout still waits for it.
 *
 * Everything fails open: a failed or slow (5 s) rules read lets the add or
 * the checkout go ahead, and a page without this script works as before.
 * Products not sold in packs are never touched.
 */
(function () {
  'use strict';

  if (window.LunaPackRule) {
    return;
  }

  var R = {};
  window.LunaPackRule = R;

  R.timeoutMs = 5000;        // rules read: longer than this and we fail open
  R.writeTimeoutMs = 5000;   // our own /cart/change.js: stop waiting after this
  R.settleMaxMs = 4000;      // checkout waits at most this long for quantity debounces
  R.busyCapMs = 10000;       // ... and this long for cart writes in flight

  function num(value) {
    var n = parseInt(value, 10);
    return isNaN(n) ? 0 : n;
  }

  function log(err) {
    if (window.console && console.warn) {
      console.warn('[pack rule]', err);
    }
  }

  // The early hook's shared state (snippets/pack-rule-hook.liquid). Without
  // it (a layout that does not render the hook), nothing is watched: app
  // adds are not checked and checkout does not wait for writes.
  var S = window.__lunaPack;
  if (!S) {
    S = window.__lunaPack = { inflight: 0, bg: 0, queue: [], own: [], run: null };
    log('pack rules: the early hook is missing; app adds are not checked');
  }

  /* ------------------------------------------------------------------
     The rule (pure). Mirrors snippets/pack-rule-check.liquid.
     available: the stock left, or null / '' / negative for no limit.
     ------------------------------------------------------------------ */

  // The stock limit as a number, or null when there is none.
  function known(available) {
    if (available === null || available === undefined || available === '') return null;
    var n = parseInt(available, 10);
    return isNaN(n) || n < 0 ? null : n;
  }
  R.known = known;

  // What Shopify leaves in the cart when asked for total: capped at the stock left.
  function capped(total, available) {
    var a = known(available);
    return a === null ? total : Math.min(total, a);
  }

  R.isPack = function (size) {
    return num(size) > 1;
  };

  R.isValid = function (total, size, single, available) {
    total = num(total);
    size = num(size);
    var a = known(available);
    if (size < 2 || total === 0) return true;
    if (total === 1 && single) return true;
    if (a !== null && a > 0 && total === a) return true;   // all the stock left
    return total > 0 && total % size === 0;
  };

  // The next valid total above this one that the stock allows, or 0.
  R.nextUp = function (total, size, single, available) {
    total = num(total);
    size = num(size);
    var a = known(available);
    if (size < 2) return 0;
    var next = (Math.floor(Math.max(total, 0) / size) + 1) * size;
    if (total < 1 && single) next = 1;
    if (a === null || next <= a) return next;
    return a > total ? a : 0;
  };

  // The largest valid total below this one that is above 0 and that the
  // stock allows, or 0 for none. With less stock left than the total, that
  // is the stock left itself (all of it is valid), and 0 when sold out:
  // only Remove.
  R.nextDown = function (total, size, single, available) {
    total = num(total);
    size = num(size);
    var a = known(available);
    if (size < 2) return 0;
    if (a !== null && a < total) return a;
    var rem = total % size;
    var below = rem === 0 ? total - size : total - rem;
    var best = below > 0 ? below : 0;
    if (!best && total > 1 && single) best = 1;
    return best;
  };

  // The cart already holds all the stock left (some is left): nothing can
  // be added. A single counts too: with 1 left it is all of it.
  R.allInCart = function (total, available) {
    var a = known(available);
    return a !== null && a > 0 && num(total) >= a;
  };

  // Quantities the product page can add to an existing total and stay
  // valid: 1 (singles allowed, nothing in the cart) and whole packs.
  R.allowedAddQuantities = function (existing, size, single, maxPacks, available) {
    existing = num(existing);
    size = num(size);
    maxPacks = maxPacks || 400;
    var candidates = [];
    if (size < 2) return candidates;
    if (single) candidates.push(1);
    for (var i = 1; i <= maxPacks; i++) candidates.push(i * size);
    return candidates.filter(function (q) { return R.isValid(existing + q, size, single, available); });
  };

  // The product page's dropdown for a cart total, as snippets/
  // quantity-dropdown-pack.liquid draws it: [{qty, all}], all = the "13 (all
  // remaining)" option. A single in the cart is replaced, so it doesn't
  // count against the stock. Without a stock limit: 1 (singles allowed,
  // nothing in the cart) and whole packs up to maxPacks. Nothing at all when
  // the cart holds all the stock left (R.allInCart); one pack when sold out.
  R.addOptions = function (total, size, single, available, maxPacks) {
    total = num(total);
    size = num(size);
    maxPacks = maxPacks || 400;
    var a = known(available);
    var out = [];
    if (size < 2) return out;
    var counted = total === 1 ? 0 : total;
    var room = a === null ? maxPacks * size : a - counted;
    var offerOne = !!single && total === 0 && room >= 1;
    if (offerOne) out.push({ qty: 1 });
    for (var i = 1; i <= maxPacks && i * size <= room; i++) out.push({ qty: i * size });
    if (a !== null && room > 0 && room % size !== 0 && !(room === 1 && (offerOne || total === 1))) {
      out.push({ qty: room, all: true });
    }
    if (!out.length && !R.allInCart(total, available)) out.push({ qty: size });
    return out;
  };

  /* ------------------------------------------------------------------
     Cart helpers
     ------------------------------------------------------------------ */

  function routes() {
    return (window.theme && window.theme.routes) || {};
  }

  R.rulesUrl = function () {
    var base = routes().cartPage || '/cart';
    return base + (base.indexOf('?') === -1 ? '?' : '&') + 'view=pack-rules&t=' + Date.now();
  };

  // Settle with promise, or reject after ms with an Error marked timeout.
  function withTimeout(promise, ms, what, onTimeout) {
    var timer;
    var timeout = new Promise(function (resolve, reject) {
      timer = setTimeout(function () {
        if (onTimeout) onTimeout();
        var err = new Error('pack rules: ' + what + ' timed out');
        err.timeout = true;
        reject(err);
      }, ms);
    });
    return Promise.race([promise, timeout]).then(function (value) {
      clearTimeout(timer);
      return value;
    }, function (err) {
      clearTimeout(timer);
      throw err;
    });
  }

  // Fresh rules for the cart; rejects on error or after timeoutMs.
  R.fetchRules = function (timeoutMs) {
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    // No "Accept: application/json" here: Shopify answers a /cart?view=...
    // request that asks for JSON with the plain /cart.js body instead of the
    // alternate template (checked on the live store 2026-10-13), which made
    // every check fail open.
    var request = window.fetch(R.rulesUrl(), {
      credentials: 'same-origin',
      cache: 'no-store',
      signal: ctrl ? ctrl.signal : undefined
    }).then(function (res) {
      if (!res.ok) throw new Error('pack rules: HTTP ' + res.status);
      return res.text();
    }).then(function (text) {
      // Parse only the JSON object, in case Shopify adds anything around a
      // layout-none response (e.g. the preview bar on an unpublished theme).
      var start = text.indexOf('{');
      var end = text.lastIndexOf('}');
      var data = JSON.parse(start >= 0 && end > start ? text.slice(start, end + 1) : text);
      if (!data || !data.products || !data.items) throw new Error('pack rules: bad payload');
      return data;
    });
    return withTimeout(request, timeoutMs || R.timeoutMs, 'read', function () {
      if (ctrl) {
        try { ctrl.abort(); } catch (e) { /* ignore */ }
      }
    });
  };

  R.rowsFor = function (rules, productId) {
    return (rules.items || []).filter(function (item) {
      return String(item.product_id) === String(productId);
    });
  };

  R.productFor = function (rules, productId) {
    return (rules.products || {})[String(productId)] || null;
  };

  // Shopify's words from an error body ({status: 422, message, description}).
  function describe(data) {
    if (!data) return '';
    if (typeof data.description === 'string' && data.description) return data.description;
    return typeof data.message === 'string' ? data.message : '';
  }

  // The text to show for a failed write: Shopify's, or the element's
  // data-msg-error (a timeout or a network error has no words of its own).
  function errorText(err, el) {
    return (err && !err.timeout && describe(err.data)) || (el && el.getAttribute('data-msg-error')) || '';
  }

  function readJsonResponse(res) {
    return res.text().then(function (text) {
      var data = null;
      try { data = JSON.parse(text); } catch (e) { /* not JSON */ }
      if (!res.ok || !data || data.status === 422 || data.status === 'bad_request') {
        var err = new Error(describe(data) || ('HTTP ' + res.status));
        err.status = res.status;
        err.data = data;
        throw err;
      }
      return data;
    });
  }

  // POST /cart/change.js for one row, by line key. Resolves with the cart.
  // Stops waiting after writeTimeoutMs (rejects with err.timeout), so the
  // buttons waiting on it come back; the request is not cancelled (the
  // server may still apply it), so the hook keeps counting it in flight
  // and checkout keeps waiting for it.
  R.changeLine = function (key, quantity) {
    var post = window.fetch(routes().cartChange || '/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(readJsonResponse);
    post.catch(function () { /* handled by the caller, or late after a timeout */ });
    return withTimeout(post, R.writeTimeoutMs, 'cart change');
  };

  // Set a product's first row to target and its other rows to 0, one at a
  // time. target 0 removes the product.
  R.setProductTotal = function (rows, target) {
    var chain = Promise.resolve();
    rows.forEach(function (row, i) {
      var qty = i === 0 ? target : 0;
      if (i > 0 && num(row.quantity) === 0) return;
      chain = chain.then(function () { return R.changeLine(row.key, qty); });
    });
    return chain;
  };

  // The checkout gate's record on the order, with the setting off only.
  // Two underscores: a private attribute, hidden at checkout and not
  // readable back through Liquid or the Ajax API.
  R.warningAttribute = '__pack_rule_warning';
  R.setWarning = function (value) {
    var attributes = {};
    attributes[R.warningAttribute] = value;
    var post = window.fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).then(readJsonResponse);
    // Never hold checkout on the attribute for long.
    var timeout = new Promise(function (resolve) { setTimeout(resolve, 3000); });
    return Promise.race([post, timeout]);
  };

  R.dispatch = function (name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail }));
  };

  R.isCartPage = function () {
    return !!(document.body && document.body.classList.contains('template-cart'));
  };

  R.reload = function () {
    window.location.reload();
  };

  function fill(template, values) {
    return String(template || '').replace(/\[(\w+)\]/g, function (match, name) {
      return values[name] !== undefined ? String(values[name]) : match;
    });
  }

  /* ------------------------------------------------------------------
     Cart work in flight, and pending quantity edits (for checkout)
     ------------------------------------------------------------------ */

  var pendingUntil = 0;

  R.state = function () {
    return { inflight: S.inflight, bg: S.bg, queued: S.queue.length, pendingUntil: pendingUntil, ownAdds: S.own.length };
  };

  // Count a piece of background work (a chain of writes and reads) as cart
  // work until it settles, so checkout waits for all of it, not just for
  // each write.
  function track(promise) {
    S.bg++;
    var done = function () { S.bg = Math.max(0, S.bg - 1); };
    promise.then(done, done);
    return promise;
  }

  // theme.QtySelector posts a cart quantity 500 ms after a select changes
  // and 2 s after typing stops; remember when that will have happened. A
  // second quick edit can only push that later, never sooner.
  document.addEventListener('input', function (evt) {
    var el = evt.target;
    if (!el || el.name !== 'updates[]') return;
    pendingUntil = Math.max(pendingUntil, Date.now() + (el.tagName === 'INPUT' ? 2000 : 500) + 150);
    // They are changing a quantity, as the "Already in your cart" note asks:
    // the rebuild that follows need not draw it again.
    alreadyNotes = {};
  }, true);

  function writesBusy() {
    return S.inflight > 0 || S.bg > 0 || S.queue.length > 0;
  }

  // Resolves 'idle' once there is nothing to wait for (at once if nothing is
  // pending), or 'busy' if cart writes are still in flight after capMs.
  // Pending debounces alone are waited out for at most settleMs: they have
  // not written anything yet.
  R.waitForCartWork = function (settleMs, capMs) {
    var start = Date.now();
    settleMs = settleMs || R.settleMaxMs;
    capMs = capMs || R.busyCapMs;
    return new Promise(function (resolve) {
      (function poll() {
        var now = Date.now();
        if (writesBusy()) {
          if (now - start >= capMs) {
            resolve('busy');
            return;
          }
        } else if (now >= pendingUntil || now - start >= settleMs) {
          resolve('idle');
          return;
        }
        setTimeout(poll, 50);
      })();
    });
  };

  // The product form is about to post its own add: the hook lets it pass
  // unless Shopify refuses it.
  function markOwnAdd() {
    S.own.push(Date.now() + 10000);
  }

  /* ------------------------------------------------------------------
     Product page
     ------------------------------------------------------------------ */

  function packEl(form) {
    return form && form.querySelector ? form.querySelector('[data-pack-rule]') : null;
  }

  function qtySelect(form) {
    if (!form) return null;
    var sel = form.querySelector('[name="quantity"]');
    if (!sel && form.id) {
      sel = document.querySelector('[name="quantity"][form="' + form.id + '"]');
    }
    return sel;
  }

  function info(el) {
    return {
      productId: el.getAttribute('data-product-id'),
      size: num(el.getAttribute('data-pack-size')),
      single: el.getAttribute('data-pack-single') === 'true',
      total: num(el.getAttribute('data-in-cart')),
      available: known(el.getAttribute('data-pack-available'))
    };
  }

  function setMessage(el, text) {
    var box = el.querySelector('[data-pack-message]');
    if (box) box.textContent = text || '';
  }

  // Can qty replace a single on its own? What Shopify would leave (capped
  // at the stock left) must be a valid total above 1.
  function replaceOk(qty, p) {
    var after = capped(qty, p.available);
    return after > 1 && R.isValid(after, p.size, p.single, p.available);
  }

  // What Add would replace the single with: the dropdown's choice, or the
  // first valid replacement if the choice could not replace it; capped at
  // the stock left, as Shopify will.
  function replaceTarget(el) {
    var p = info(el);
    var sel = qtySelect(el.closest('form'));
    var qty = num(sel && sel.value);
    if (replaceOk(qty, p)) return capped(qty, p.available);
    return R.nextUp(1, p.size, false, p.available) || p.size;
  }

  // The note under Add to cart, from data-in-cart and the dropdown.
  function drawNote(el) {
    var p = info(el);
    var note = el.querySelector('[data-pack-note]');
    var noteText = el.querySelector('[data-pack-note-text]');
    var link = el.querySelector('[data-pack-open-cart]');
    var text = '';
    var showLink = false;
    var valid = R.isValid(p.total, p.size, p.single, p.available);
    if (valid && R.allInCart(p.total, p.available)) {
      text = fill(el.getAttribute('data-msg-all-in-cart'), { qty: p.available });
    } else if (p.total === 1) {
      text = fill(el.getAttribute('data-msg-replace'), { qty: replaceTarget(el) });
    } else if (!valid) {
      text = fill(el.getAttribute('data-msg-invalid'), { total: p.total });
      showLink = true;
    }
    if (noteText) noteText.textContent = text;
    if (note) note.hidden = !text;
    if (link) link.hidden = !showLink;
  }

  // The dropdown's options for the element's state (R.addOptions), redrawn
  // only when they differ; the choice is kept if it is still offered. When
  // the cart holds all the stock left there are none: the dropdown is
  // disabled and hidden until the total drops.
  function drawOptions(el, sel) {
    var p = info(el);
    var full = R.allInCart(p.total, p.available);
    var wrapper = sel.closest('.js-qty__wrapper');
    sel.disabled = full;
    if (wrapper) wrapper.hidden = full;
    var label = el.getAttribute('data-msg-all-remaining') || '[qty]';
    var want = R.addOptions(p.total, p.size, p.single, p.available).map(function (o) {
      return [String(o.qty), o.all ? fill(label, { qty: o.qty }) : String(o.qty)];
    });
    var have = Array.prototype.map.call(sel.options, function (o) { return [o.value, o.textContent]; });
    if (JSON.stringify(want) === JSON.stringify(have)) return;
    var chosen = sel.value;
    while (sel.firstChild) sel.removeChild(sel.firstChild);
    want.forEach(function (w) {
      var opt = document.createElement('option');
      opt.value = w[0];
      opt.textContent = w[1];
      sel.appendChild(opt);
    });
    var still = want.some(function (w) { return w[0] === chosen; });
    if (want.length) sel.value = still ? chosen : want[0][0];
  }

  // Add to cart, disabled while the cart holds all the stock left; given
  // back only if it was this that disabled it (data-pack-full), so the
  // theme's own sold-out state is never touched.
  function drawAddButton(el) {
    var p = info(el);
    var form = el.closest('form');
    var btn = form && form.querySelector('[data-add-to-cart]');
    if (!btn) return;
    if (R.allInCart(p.total, p.available)) {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('data-pack-full', '');
    } else if (btn.hasAttribute('data-pack-full')) {
      btn.disabled = false;
      btn.removeAttribute('aria-disabled');
      btn.removeAttribute('data-pack-full');
    }
  }

  var stateDraws = 0;   // setProductState calls (a slow refresh must not undo a newer draw)

  // Redraw the dropdown and notes for a new in-cart total (and stock left,
  // when the caller has a fresh one: undefined keeps the page's).
  R.setProductState = function (el, total, available) {
    stateDraws++;
    total = num(total);
    el.setAttribute('data-in-cart', String(total));
    if (available !== undefined) {
      var a = known(available);
      el.setAttribute('data-pack-available', a === null ? '' : String(a));
    }
    var sel = qtySelect(el.closest('form'));
    if (sel) drawOptions(el, sel);
    drawNote(el);
    drawAddButton(el);
    setMessage(el, '');
  };

  // Every pack block on the page, from a fresh read.
  function redrawProducts(rules) {
    document.querySelectorAll('[data-pack-rule]').forEach(function (el) {
      var prod = R.productFor(rules, info(el).productId);
      R.setProductState(el, prod ? prod.total : 0, prod ? prod.available : undefined);
    });
  }

  R.refreshProduct = function (el) {
    return R.fetchRules().then(function (rules) {
      var prod = R.productFor(rules, info(el).productId);
      R.setProductState(el, prod ? prod.total : 0, prod ? prod.available : undefined);
    }, log);
  };

  // After a cart write or add that failed or was refused, which may still
  // have changed the cart (Shopify caps a quantity at the stock left and
  // applies it before answering 422): read the cart again and redraw the
  // product page, then rebuild the cart lists (their flags come from the
  // rebuild). Resolves with the fresh rules, or null if the read failed.
  // withRules(rules), if given, runs on the fresh read before the rebuild
  // (to note a row for it).
  R.resync = function (withRules) {
    return track(R.fetchRules().then(function (rules) {
      redrawProducts(rules);
      if (withRules) withRules(rules);
      return rules;
    }, function (err) {
      log(err);
      return null;
    }).then(function (rules) {
      watchCartLists();
      dispatchQuiet('cart:build');   // the product page is drawn already
      return rules;
    }));
  };

  // Replace the single in the cart with qty, for an Add pressed while the
  // page says "Your single will be replaced with ...": one /cart/change.js
  // on the single's row (its key from the fresh read), then any other row of
  // the product to 0. Never add-then-fix. Always resolves false, so the theme
  // posts nothing itself and takes the loading state off its Add button.
  function replaceSingle(el, rules, qty) {
    var p = info(el);
    var rows = R.rowsFor(rules, p.productId);
    var single = rows.filter(function (row) { return num(row.quantity) === 1; })[0];
    var others = rows.filter(function (row) { return row !== single && num(row.quantity) > 0; });
    if (!single) return Promise.reject(new Error('pack rules: no single row to replace'));
    return track(R.changeLine(single.key, qty).then(function () {
      var chain = Promise.resolve();
      others.forEach(function (row) {
        chain = chain.then(function () { return R.changeLine(row.key, 0); });
      });
      return chain;
    })).then(function () {
      R.setProductState(el, qty);
      dispatchQuiet('cart:build');
      R.dispatch('cart:open');
      return false;
    });
  }

  // A replace that failed or was refused: say so, and redraw from a fresh
  // read. If the cart did change (Shopify capped the quantity at the stock
  // left and applied it), that is the replacement done: open the cart.
  function replaceFailed(el, err) {
    log(err);
    var text = errorText(err, el);
    setMessage(el, text);
    R.resync().then(function (rules) {
      if (!rules) return;
      var prod = R.productFor(rules, info(el).productId);
      if (prod && num(prod.total) !== 1) R.dispatch('cart:open');
      setMessage(el, text);
    });
    return false;
  }

  var ownAdd = null;   // {el, before}: the product form's add being posted

  // Called by theme.AjaxProduct.addItemFromForm before it posts
  // /cart/add.js. Resolves true to let the add go ahead; false when it has
  // refused the add or done the work itself (replacing a single).
  R.beforeAdd = function (form) {
    try {
      ownAdd = null;
      var el = packEl(form);
      if (!el) {
        markOwnAdd();
        return Promise.resolve(true);
      }
      var p = info(el);
      var sel = qtySelect(form);
      var qty = num(sel && sel.value) || 1;
      setMessage(el, '');
      el._packExpected = null;

      return R.fetchRules().then(function (rules) {
        var prod = R.productFor(rules, p.productId);
        var total = prod ? num(prod.total) : 0;
        var now = { size: p.size, single: p.single, available: prod ? prod.available : p.available };
        if (total === 1 && p.total === 1) {
          // The page told them their single will be replaced, and it is
          // still there: replace it with a valid quantity, or refuse.
          if (replaceOk(qty, now)) {
            return replaceSingle(el, rules, qty).catch(function (err) {
              return replaceFailed(el, err);
            });
          }
        } else {
          // Judged on what Shopify will leave: an add above the stock left
          // is capped at it, and all the stock left is valid.
          var after = capped(total + qty, now.available);
          if (!R.allInCart(total, now.available) && R.isValid(after, p.size, p.single, now.available)) {
            el._packExpected = after;
            ownAdd = { el: el, before: total };
            markOwnAdd();
            return true;
          }
        }
        // Refused. A single the page did not know about (another tab) lands
        // here too: the redraw shows the replace note before any replacing.
        // With all the stock left already in the cart, the redrawn note
        // says so ("You already have all 13 ...") and Add is disabled.
        R.setProductState(el, total, prod ? prod.available : undefined);
        if (!R.allInCart(total, now.available)) {
          setMessage(el, fill(el.getAttribute('data-msg-refused'), { qty: qty, total: total + qty }));
        }
        return false;
      }, function (err) {
        log(err);           // fail open: the cart warnings still catch it
        markOwnAdd();
        return true;
      });
    } catch (err) {
      log(err);
      return Promise.resolve(true);
    }
  };

  // The product form's own add came back refused (the hook queued it).
  // theme.js has already shown Shopify's message as an error. Redraw from a
  // fresh read; if the cart did change, Shopify capped the add at the stock
  // left and added that much, which is a success under the remainder rule:
  // show its message as a note instead of an error, and open the cart.
  function afterOwnAddFailed(res) {
    var add = ownAdd;
    ownAdd = null;
    return res.text().then(function (text) {
      try { return JSON.parse(text); } catch (e) { return null; }
    }, function () { return null; }).then(function (data) {
      return R.resync().then(function (rules) {
        if (!add) return;
        var el = add.el;
        el._packExpected = null;
        if (!rules) return;
        var prod = R.productFor(rules, info(el).productId);
        if ((prod ? num(prod.total) : 0) === add.before) return;   // nothing added: the error stands
        var form = el.closest('form');
        var errors = form && form.querySelector('.errors');
        if (errors && errors.parentNode) errors.parentNode.removeChild(errors);
        setMessage(el, describe(data));
        R.dispatch('cart:open');
      });
    });
  }

  // After the theme's own add succeeds, redraw from the new total.
  document.addEventListener('ajaxProduct:added', function (evt) {
    var form = evt.target && evt.target.closest ? evt.target.closest('form') : null;
    var el = packEl(form);
    if (!el) return;
    if (ownAdd && ownAdd.el === el) ownAdd = null;
    if (el._packExpected !== null && el._packExpected !== undefined) {
      R.setProductState(el, el._packExpected);
      el._packExpected = null;
    } else {
      R.refreshProduct(el);
    }
  });

  document.addEventListener('change', function (evt) {
    var sel = evt.target;
    if (!sel || sel.name !== 'quantity' || !sel.form) return;
    var el = packEl(sel.form);
    if (el) drawNote(el);   // "replaced with <qty>" follows the dropdown
  });

  // The product page goes stale while it is open: the drawer changed the
  // cart (theme.js fires cart:updated after a quantity change or a remove,
  // and cart:build rebuilds the lists, after an app's add too), or the page
  // is shown again (the back/forward cache, another tab). Then read the cart
  // once and redraw every pack block. One read per burst of events
  // (debounced), none on the first load (Liquid drew it) and none on a page
  // without a pack block; our own dispatches that follow a redraw are quiet.
  R.refreshDelayMs = 250;
  var refreshTimer = null;
  var quiet = false;

  function dispatchQuiet(name) {
    quiet = true;
    try { R.dispatch(name); } finally { quiet = false; }
  }

  function scheduleRefresh() {
    if (quiet || !document.querySelector('[data-pack-rule]')) return;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      var seen = stateDraws;
      R.fetchRules().then(function (rules) {
        if (stateDraws === seen) redrawProducts(rules);   // else a newer draw stands
      }, log);
    }, R.refreshDelayMs);
  }

  document.addEventListener('cart:updated', scheduleRefresh);
  document.addEventListener('cart:build', scheduleRefresh);
  window.addEventListener('pageshow', function (evt) {
    if (evt.persisted) scheduleRefresh();
  });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') scheduleRefresh();
  });

  /* ------------------------------------------------------------------
     Cart page and drawer: fix buttons
     ------------------------------------------------------------------ */

  function setFlagBusy(flag, busy) {
    flag.querySelectorAll('[data-pack-fix]').forEach(function (b) { b.disabled = busy; });
  }

  function afterCartFix() {
    R.dispatch('cart:build');
    if (R.isCartPage()) R.reload();
  }

  R.fix = function (btn) {
    var flag = btn.closest('[data-pack-flag]');
    if (!flag) return Promise.resolve(false);
    var productId = flag.getAttribute('data-product-id');
    var flaggedTotal = num(flag.getAttribute('data-pack-total'));
    var target = num(btn.getAttribute('data-target'));
    var msg = flag.querySelector('[data-pack-flag-message]');
    if (msg) msg.textContent = '';
    setFlagBusy(flag, true);

    return track(R.fetchRules().then(function (rules) {
      var prod = R.productFor(rules, productId);
      if (!prod || num(prod.total) !== flaggedTotal) {
        return 'stale';   // the cart changed elsewhere: just redraw it
      }
      return R.setProductTotal(R.rowsFor(rules, productId), target);
    }).then(function () {
      afterCartFix();
      return true;
    }, function (err) {
      // A refused, failed or timed-out write (perhaps after an earlier row
      // was already changed): show why, give the buttons back, and redraw
      // from a fresh read, in place (a reload would lose the message). The
      // rebuilt row shows the message on its flag, or as a note if the
      // total is valid now (capped at the stock left).
      log(err);
      var text = errorText(err, flag);
      if (msg) msg.textContent = text;
      setFlagBusy(flag, false);
      noteAlready(productId, true, text);
      return R.resync().then(function () { return false; });
    }));
  };

  /* ------------------------------------------------------------------
     Checkout gate (cart page form)
     ------------------------------------------------------------------ */

  function invalidProducts(rules) {
    var out = [];
    Object.keys(rules.products || {}).forEach(function (id) {
      var prod = rules.products[id];
      if (R.isPack(prod.pack_size) && !R.isValid(prod.total, prod.pack_size, prod.single_sample, prod.available)) {
        out.push(prod);
      }
    });
    return out;
  }

  R.warningText = function (products) {
    return products.map(function (prod) {
      return (prod.sku || prod.title) + ':' + prod.total + '/' + prod.pack_size;
    }).join('; ');
  };

  // The message above the checkout button (rendered empty by
  // sections/main-cart.liquid, so it is a live region from the start).
  function gateMessage(form, text) {
    var box = form.querySelector('[data-pack-checkout-msg]');
    if (!text) {
      if (box) box.textContent = '';
      return;
    }
    if (!box) {
      box = document.createElement('p');
      box.className = 'pack-rule__checkout-msg';
      box.setAttribute('data-pack-checkout-msg', '');
      box.setAttribute('role', 'alert');
      var wrapper = form.querySelector('.cart__checkout-wrapper');
      if (wrapper && wrapper.parentNode) {
        wrapper.parentNode.insertBefore(box, wrapper);
      } else {
        form.appendChild(box);
      }
    }
    box.textContent = text;
  }

  function scrollToFlag(form) {
    function go() {
      var flag = form.querySelector('[data-pack-flag]');
      if (flag && typeof flag.scrollIntoView === 'function') {
        try { flag.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { /* ignore */ }
      }
    }
    var products = form.querySelector('[data-products]');
    if (!products || typeof MutationObserver !== 'function') {
      go();
      return;
    }
    // cart:build redraws the rows; scroll once the new ones are in.
    var timer;
    var observer = new MutationObserver(function () {
      observer.disconnect();
      clearTimeout(timer);
      setTimeout(go, 0);
    });
    observer.observe(products, { childList: true });
    timer = setTimeout(function () {
      observer.disconnect();
      go();
    }, 3000);
  }

  // Disable the form's enabled updates[] fields; returns them, to give back.
  function holdUpdates(form) {
    var fields = Array.prototype.slice.call(form.querySelectorAll('[name="updates[]"]'));
    var held = fields.filter(function (f) { return !f.disabled; });
    held.forEach(function (f) { f.disabled = true; });
    return held;
  }

  function releaseUpdates(held) {
    held.forEach(function (f) { f.disabled = false; });
  }

  // The browser's own submit (no submit event, so no handler runs again),
  // still without updates[] and with the checkout field the button gives.
  function nativeSubmit(form, submitter) {
    var held = holdUpdates(form);
    try {
      if (!form.querySelector('input[type="hidden"][name="checkout"]')) {
        var hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'checkout';
        hidden.value = (submitter && submitter.value) || '1';
        form.appendChild(hidden);
      }
      HTMLFormElement.prototype.submit.call(form);
    } finally {
      releaseUpdates(held);
    }
  }

  // Submit for checkout without the positional updates[] fields: each row
  // posts three, so Shopify only ignores them because the count is wrong,
  // and they could overwrite live quantities.
  R.submitForCheckout = function (form, submitter) {
    if (typeof form.requestSubmit !== 'function') {
      nativeSubmit(form, submitter);
      return;
    }
    var held = holdUpdates(form);
    form._packPass = true;
    try {
      if (submitter && submitter.form === form) {
        form.requestSubmit(submitter);
      } else {
        form.requestSubmit();
      }
    } finally {
      form._packPass = false;
      // The browser has built the form data by now; give the controls back
      // in case the navigation is cancelled or the page is restored later.
      releaseUpdates(held);
    }
  };

  // Called by theme.CartForm.onSubmit after its own checks. Returns false
  // when it has taken over the submit (it resubmits the form itself).
  R.checkoutGate = function (evt, form, submitBtn) {
    if (!form || form.id !== 'CartPageForm') return true;
    var submitter = (evt && evt.submitter) || null;
    if (submitter && submitter.name !== 'checkout') return true;
    if (form._packPass) return true;   // our own resubmit
    if (form._packOpen) return true;   // the gate broke on this page: stay out of the way

    if (evt) evt.preventDefault();
    if (form._packBusy) return false;
    form._packBusy = true;

    var button = submitter || submitBtn || form.querySelector('[name="checkout"]');
    var block = form.getAttribute('data-pack-block-checkout') === 'true';
    var finished = false;
    gateMessage(form, '');

    function release() {
      form._packBusy = false;
      if (button) button.classList.remove('btn--loading');
      if (submitBtn && submitBtn !== button) submitBtn.classList.remove('btn--loading');
    }

    function proceed() {
      if (finished) return;
      finished = true;
      form._packBusy = false;
      try {
        R.submitForCheckout(form, button);
      } catch (err) {
        // Never leave checkout dead: give the button back, switch the gate
        // off for this page, and let the browser submit the form (fail open).
        log(err);
        release();
        form._packOpen = true;
        try { nativeSubmit(form, button); } catch (e) { log(e); }
      }
    }

    function refuse(text, showFlags) {
      if (finished) return;
      finished = true;
      release();
      if (showFlags) R.dispatch('cart:build');
      gateMessage(form, text);
      if (showFlags) scrollToFlag(form);
    }

    R.waitForCartWork().then(function (state) {
      if (state === 'busy') {
        // Writes still in flight after busyCapMs: the cart may yet change,
        // so never judge it now.
        return refuse(form.getAttribute('data-pack-msg-busy') ||
          'Your cart is still updating — please wait a moment and try again.', false);
      }
      return R.fetchRules().then(function (rules) {
        var bad = invalidProducts(rules);
        if (block) {
          if (bad.length) return refuse(form.getAttribute('data-pack-msg-blocked'), true);
          return proceed();
        }
        // Setting off: record it on the order. Written on every gated
        // checkout ('' when valid clears an earlier one), since a private
        // attribute cannot be read back to see whether one is there.
        return R.setWarning(bad.length ? R.warningText(bad) : '').then(proceed, proceed);
      });
    }).catch(function (err) {
      log(err);             // fail open
      proceed();
    });

    return false;
  };

  /* ------------------------------------------------------------------
     Clicks
     ------------------------------------------------------------------ */

  document.addEventListener('click', function (evt) {
    var target = evt.target;
    if (!target || !target.closest) return;
    var fixBtn = target.closest('[data-pack-fix]');
    if (fixBtn) {
      evt.preventDefault();
      R.fix(fixBtn);
    }
  });

  /* ------------------------------------------------------------------
     Notes on a cart row, drawn after the cart lists are rebuilt:
     "Already in your cart" after an app's add is undone, and the message
     of a write that failed
     ------------------------------------------------------------------ */

  R.alreadyNoteMs = 5000;     // cart rebuilds this soon after an undo draw the note again
  R.alreadyCarryMs = 15000;   // a load of /cart this soon after an undo shows it once
  var CARRY_KEY = 'lunaPackAlreadyInCart';
  var alreadyNotes = {};      // product id -> { until, failed, error }
  var watchTimer = null;

  function storage() {
    try { return window.sessionStorage || null; } catch (e) { return null; }
  }

  function setCarry(ids) {
    var s = storage();
    if (!s || !ids.length) return;
    try {
      s.setItem(CARRY_KEY, JSON.stringify({ ids: ids, until: Date.now() + R.alreadyCarryMs }));
    } catch (e) { /* ignore */ }
  }

  function clearCarry() {
    var s = storage();
    if (!s) return;
    try { s.removeItem(CARRY_KEY); } catch (e) { /* ignore */ }
  }

  // The product ids carried across a reload, once: the key goes as it is read.
  function takeCarry() {
    var s = storage();
    if (!s) return [];
    try {
      var raw = s.getItem(CARRY_KEY);
      if (!raw) return [];
      s.removeItem(CARRY_KEY);
      var data = JSON.parse(raw);
      if (!data || !data.ids || !(num(data.until) >= Date.now())) return [];
      return data.ids.map(String);
    } catch (e) {
      return [];
    }
  }

  // Note a product for the next rebuilds of the cart lists. failed: a write
  // did not go through (an undo, a fix), so its flag gets the error instead,
  // or, when the product has no flag now, a note with the error's text.
  function noteAlready(id, failed, error) {
    if (!/^\d+$/.test(String(id))) return;
    alreadyNotes[String(id)] = { until: Date.now() + R.alreadyNoteMs, failed: !!failed, error: error || '' };
  }

  // A polite status note on a row, above its flag if it has one.
  function addRowNote(row, flag, attr, text) {
    var box = document.createElement('div');
    box.className = 'pack-already';
    box.setAttribute(attr, '');
    box.setAttribute('role', 'status');
    box.setAttribute('aria-live', 'polite');
    var holder = row.querySelector('.cart__item-title') || row;
    holder.insertBefore(box, flag && flag.parentNode === holder ? flag : null);
    // Fill the live region once it is in the page, so it is announced.
    setTimeout(function () {
      var p = document.createElement('p');
      p.className = 'pack-rule__note pack-rule__note--cart';
      p.textContent = text;
      box.appendChild(p);
    }, 50);
  }

  // Draw the pending notes into one cart list ([data-products]), on each
  // product's first row; true if anything was drawn. A row that already has
  // its note is left alone, so a list can be drawn into any number of times.
  function drawAlreadyNotes(root) {
    var now = Date.now();
    var drawn = false;
    Object.keys(alreadyNotes).forEach(function (id) {
      var note = alreadyNotes[id];
      if (note.until < now) {
        delete alreadyNotes[id];
        return;
      }
      var row = root.querySelector('.cart__item[data-product-id="' + id + '"]');
      if (!row) return;
      var flag = row.querySelector('[data-pack-flag]');
      if (note.failed) {
        // The write did not go through: the flag (drawn when the total is
        // broken) carries the error, as a failed fix does.
        var msg = flag && flag.querySelector('[data-pack-flag-message]');
        if (msg) {
          if (!msg.textContent) {
            msg.textContent = note.error || flag.getAttribute('data-msg-error') || '';
            drawn = true;
          }
          return;
        }
        // No flag: the total is valid now (Shopify capped a write at the
        // stock left), so Shopify's words become a note on the row.
        if (note.error && !row.querySelector('[data-pack-info]')) {
          addRowNote(row, flag, 'data-pack-info', note.error);
          drawn = true;
        }
        return;
      }
      var text = row.getAttribute('data-msg-already');
      if (!text || row.querySelector('[data-pack-already]')) return;
      addRowNote(row, flag, 'data-pack-already', text);
      drawn = true;
    });
    return drawn;
  }

  function notesUntil() {
    var until = 0;
    Object.keys(alreadyNotes).forEach(function (id) { until = Math.max(until, alreadyNotes[id].until); });
    return until;
  }

  // theme.js CartForm.buildCart replaces a [data-products] list's children
  // and says nothing when it is done, so watch every list (drawer and cart
  // page) for that, until the pending notes run out.
  function watchCartLists() {
    var until = notesUntil();
    if (!until) return;
    if (typeof MutationObserver === 'function') {
      document.querySelectorAll('[data-products]').forEach(function (root) {
        if (root._packAlreadyObserver) return;
        var observer = new MutationObserver(function () {
          if (drawAlreadyNotes(root)) clearCarry();   // shown here: no reload needs it now
        });
        observer.observe(root, { childList: true });
        root._packAlreadyObserver = observer;
      });
    }
    clearTimeout(watchTimer);
    watchTimer = setTimeout(stopWatching, Math.max(0, until - Date.now()) + 50);
  }

  function stopWatching() {
    if (notesUntil() > Date.now()) {
      watchCartLists();
      return;
    }
    alreadyNotes = {};
    document.querySelectorAll('[data-products]').forEach(function (root) {
      if (root._packAlreadyObserver) {
        root._packAlreadyObserver.disconnect();
        root._packAlreadyObserver = null;
      }
    });
  }

  // A reload onto the cart page soon after an undo (an app that reloads or
  // goes to /cart after its add): show the note there, once.
  function showCarried() {
    if (!R.isCartPage()) return;
    var ids = takeCarry();
    if (!ids.length) return;
    ids.forEach(function (id) { noteAlready(id); });
    document.querySelectorAll('[data-products]').forEach(drawAlreadyNotes);
    watchCartLists();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', showCarried);
  } else {
    showCarried();
  }

  /* ------------------------------------------------------------------
     Adds from outside the product form, queued by the early hook
     ------------------------------------------------------------------ */

  function addQty(value) {
    return value === undefined || value === null || value === '' ? 1 : num(value);
  }

  function addListFrom(obj) {
    var list = obj && obj.items ? obj.items : (obj && obj.id !== undefined ? [obj] : null);
    if (!list || !list.length) return null;
    return list.map(function (it) {
      return { id: String(it && it.id), quantity: addQty(it && it.quantity) };
    });
  }

  function addListFromPairs(params) {
    var single = {};
    var items = [];
    params.forEach(function (value, key) {
      var m = /^items\[(\d+)\]\[(id|quantity)\]$/.exec(key);
      if (m) {
        items[m[1]] = items[m[1]] || {};
        items[m[1]][m[2]] = value;
      } else if (key === 'id' || key === 'quantity') {
        single[key] = value;
      }
    });
    items = items.filter(Boolean);
    if (items.length) return addListFrom({ items: items });
    return single.id !== undefined ? addListFrom(single) : null;
  }

  // What an add asked for, from its request body: [{id, quantity}] in
  // request order (quantity 1 when not given), or null when it can't be
  // read. JSON ({items: [...]} or {id, quantity}), form-encoded, FormData.
  R.parseAddBody = function (body) {
    try {
      if (body === undefined || body === null) return null;
      if (typeof body === 'string') {
        var text = body.trim();
        if (text.charAt(0) === '{') return addListFrom(JSON.parse(text));
        return typeof URLSearchParams === 'function' ? addListFromPairs(new URLSearchParams(text)) : null;
      }
      if (typeof URLSearchParams === 'function' && body instanceof URLSearchParams) return addListFromPairs(body);
      if (typeof FormData === 'function' && body instanceof FormData) return addListFromPairs(body);
    } catch (e) {
      log(e);
    }
    return null;
  };

  // How many of the response's item i this add asked for, or null.
  function addedQuantity(request, items, i) {
    if (!request) return null;
    var vid = String(items[i].variant_id || items[i].id);
    if (request.length === items.length && request[i] && request[i].id === vid) return request[i].quantity;
    var same = request.filter(function (r) { return r.id === vid; });
    return same.length === 1 ? same[0].quantity : null;
  }

  // Undo an add of a product that was already in the cart: per line the add
  // landed on, ONE /cart/change.js back to what the line held before it (its
  // quantity now less the added quantity: 0 removes a row the add created).
  // Resolves 'undone', 'failed' or 'skipped' (the line is not in the read).
  function undoAdd(id, entries, rows) {
    var perKey = {};
    var keys = [];
    entries.forEach(function (e) {
      if (!Object.prototype.hasOwnProperty.call(perKey, e.item.key)) {
        perKey[e.item.key] = 0;
        keys.push(e.item.key);
      }
      perKey[e.item.key] += e.qty;
    });
    var changes = [];
    keys.forEach(function (key) {
      var row = rows.filter(function (r) { return r.key === key; })[0];
      if (row) changes.push({ key: key, quantity: Math.max(0, num(row.quantity) - perKey[key]) });
    });
    if (!changes.length) {
      log('pack rules: the added line is not in the cart read; left for the flags');
      return Promise.resolve('skipped');
    }
    var chain = Promise.resolve();
    changes.forEach(function (change) {
      chain = chain.then(function () {
        return R.changeLine(change.key, change.quantity);
      }).then(function (cart) {
        R.dispatch('cart:updated', { cart: cart });
      });
    });
    return chain.then(function () {
      noteAlready(id);
      return 'undone';
    }, function (err) {
      log(err);   // the add stands; its flag shows the error
      noteAlready(id, true, err && describe(err.data));
      return 'failed';
    });
  }

  // Any add from outside the product-form flow (the Also Bought app, the
  // grid quick add), after Shopify accepted it; request is R.parseAddBody's
  // list, or null. See the header: a pack product already in the cart has
  // the add undone; one that wasn't keeps today's rule (a packs-only lone
  // single goes up to the next valid total). Anything else is left alone,
  // and the cart is rebuilt so its warnings show; after a failed write it
  // is read again and redrawn first.
  R.afterForeignAdd = function (data, request) {
    var added = data && data.items ? data.items : (data && data.key ? [data] : []);
    if (!added.length) return Promise.resolve();
    return R.fetchRules().then(function (rules) {
      var groups = {};
      var order = [];
      added.forEach(function (item, i) {
        var prod = R.productFor(rules, item.product_id);
        if (!prod || !R.isPack(prod.pack_size)) return;
        var id = String(item.product_id);
        if (!groups[id]) {
          groups[id] = { prod: prod, entries: [] };
          order.push(id);
        }
        groups[id].entries.push({ item: item, qty: addedQuantity(request, added, i) });
      });
      if (!order.length) return;

      var chain = Promise.resolve();
      var undone = [];
      var openCart = false;
      var failed = false;
      order.forEach(function (id) {
        var prod = groups[id].prod;
        var entries = groups[id].entries;
        var rows = R.rowsFor(rules, id);
        var qtyKnown = entries.every(function (e) { return e.qty !== null; });
        var addedQty = entries.reduce(function (sum, e) { return sum + (e.qty || 0); }, 0);
        var before = num(prod.total) - addedQty;
        if (qtyKnown && addedQty > 0 && before > 0) {
          chain = chain.then(function () {
            return undoAdd(id, entries, rows);
          }).then(function (outcome) {
            if (outcome === 'undone') undone.push(id);
            if (outcome === 'failed') failed = true;
            if (outcome !== 'skipped') openCart = true;
          });
          return;
        }
        var size = num(prod.pack_size);
        entries.forEach(function (e) {
          var item = e.item;
          if (!prod.single_sample && rows.length === 1 && rows[0].key === item.key && num(rows[0].quantity) === 1 &&
              !R.isValid(1, size, false, prod.available)) {
            var target = R.nextUp(1, size, false, prod.available);
            if (target < 2) return;
            chain = chain.then(function () {
              return R.changeLine(item.key, target);
            }).then(function (cart) {
              R.dispatch('cart:updated', { cart: cart });
            }, function (err) {
              // The bump did not go through: after the redraw, the flag (or
              // a note, if the total is valid now) shows why.
              log(err);
              noteAlready(id, true, err && !err.timeout ? describe(err.data) : '');
              failed = true;
            });
          }
        });
      });
      return chain.then(function () {
        setCarry(undone);
        var redraw = failed ? R.resync() : Promise.resolve();
        return redraw.then(function () {
          if (!failed) {
            watchCartLists();         // before the rebuild it waits for
            R.dispatch('cart:build');
          }
          if (openCart) R.dispatch('cart:open');
        });
      });
    }, log);
  };

  // One queued add response (see snippets/pack-rule-hook.liquid).
  function handleQueued(job) {
    if (job.own) return afterOwnAddFailed(job.res);
    var request = Promise.resolve(job.body).then(R.parseAddBody, function () { return null; });
    if (!job.res.ok) {
      // An app's add Shopify refused: it may still have added some (capped
      // at the stock left), so read the cart again and redraw. Shopify's
      // words go on the row of each pack product the add asked for (by its
      // variant, in the fresh read) and the cart opens, as for an undo.
      var said = job.res.text().then(function (text) {
        try { return describe(JSON.parse(text)); } catch (e) { return ''; }
      }, function () { return ''; });
      return Promise.all([said, request]).then(function (both) {
        var noted = false;
        return R.resync(function (rules) {
          if (!both[0] || !both[1]) return;
          both[1].forEach(function (asked) {
            var item = (rules.items || []).filter(function (it) { return String(it.variant_id) === asked.id; })[0];
            var prod = item && R.productFor(rules, item.product_id);
            if (prod && R.isPack(prod.pack_size)) {
              noteAlready(item.product_id, true, both[0]);
              noted = true;
            }
          });
        }).then(function () {
          if (noted) R.dispatch('cart:open');
        });
      });
    }
    return Promise.all([job.res.json(), request]).then(function (both) {
      return R.afterForeignAdd(both[0], both[1]);
    });
  }

  // Work through the queue one job at a time; each job counts as cart work
  // (S.bg, raised by the hook) until it is done.
  var jobs = Promise.resolve();
  function drain() {
    while (S.queue.length) {
      (function (job) {
        jobs = jobs.then(function () {
          return handleQueued(job);
        }).catch(log).then(function () {
          S.bg = Math.max(0, S.bg - 1);
        });
      })(S.queue.shift());
    }
  }
  S.run = drain;
  drain();   // adds made before this file loaded
})();
